import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Trash2, ExternalLink, Search, Check } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../layout/Navbar';
import { testDataApi } from '../../../api/testdata';
import { testsuiteApi } from '../../../api/testsuite';

const CreateTestData = () => {
  const navigate = useNavigate();
  
  const [fixture, setFixture] = useState({
    name: '',
    description: '',
    data: [{ key: '', value: '' }]
  });

  const [environments, setEnvironments] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Test suites state
  const [testSuites, setTestSuites] = useState([]);
  const [loadingSuites, setLoadingSuites] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSuites, setSelectedSuites] = useState([]);

  useEffect(() => {
    fetchTestSuites();
  }, []);

  const fetchTestSuites = async () => {
    try {
      setLoadingSuites(true);
      const response = await testsuiteApi.getTestSuiteNames();
      
      // Handle the array format directly (the response is an array of objects with id and suite_name)
      if (Array.isArray(response)) {
        setTestSuites(response.map(suite => ({
          id: suite.id,
          name: suite.suite_name
        })));
      } else if (response && response.suites) {
        // Fallback to previous expected format if it exists
        setTestSuites(response.suites);
      } else {
        setTestSuites([]);
      }
    } catch (err) {
      console.error('Failed to fetch test suites:', err);
      setError('Failed to load test suites. ' + err.message);
    } finally {
      setLoadingSuites(false);
    }
  };

  const handleAddDataItem = () => {
    setFixture({
      ...fixture,
      data: [...fixture.data, { key: '', value: '' }]
    });
  };

  const handleRemoveDataItem = (index) => {
    const updatedData = fixture.data.filter((_, i) => i !== index);
    setFixture({
      ...fixture,
      data: updatedData
    });
  };

  const handleDataItemChange = (index, field, value) => {
    const updatedData = [...fixture.data];
    updatedData[index] = { ...updatedData[index], [field]: value };
    setFixture({
      ...fixture,
      data: updatedData
    });
  };

  const handleFixtureChange = (field, value) => {
    setFixture({
      ...fixture,
      [field]: value
    });
  };

  const toggleSuiteSelection = (suiteId) => {
    if (selectedSuites.includes(suiteId)) {
      setSelectedSuites(selectedSuites.filter(id => id !== suiteId));
    } else {
      setSelectedSuites([...selectedSuites, suiteId]);
    }
  };

  const handleSaveFixture = async () => {
    // Filter out empty data items
    const validData = fixture.data.filter(item => item.key && item.value);
    
    if (!fixture.name || validData.length === 0) {
      setError('Please provide a fixture name and at least one data item with key and value');
      return;
    }

    // Convert data array to variables object as expected by the API
    const variables = {};
    validData.forEach(item => {
      variables[item.key] = item.value;
    });

    setIsLoading(true);
    setError(null);

    try {
      if (selectedSuites.length > 0) {
        // If there are selected test suites, use the combined endpoint
        const suiteLinks = selectedSuites.map(suiteId => ({
          id: suiteId,
          is_default: false // Set all as non-default for now
        }));
        
        // Call the combined API endpoint
        const result = await testDataApi.createAndLinkFixture(
          fixture.name,
          fixture.description,
          variables,
          suiteLinks
        );
        
        console.log('Fixture created and linked successfully:', result);
      } else {
        // If no test suites selected, just create the fixture
        const result = await testDataApi.createFixtureSet(
          fixture.name,
          fixture.description,
          variables
        );
        
        console.log('Fixture created successfully:', result);
      }
      
      // Navigate back to fixtures list
      navigate('/testdata');
    } catch (err) {
      console.error('Failed to create fixture:', err);
      setError(err.response?.data?.detail || 'Failed to create fixture. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter test suites based on search query
  const filteredTestSuites = testSuites.filter(suite => 
    suite.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="flex flex-col lg:flex-row">
        {/* Sidebar - hidden on narrow screens, link shown in content area */}
        <div className="hidden lg:block w-64 flex-shrink-0 bg-gray-50 border-r border-gray-200 min-h-screen p-4">
          <div className="space-y-1">
            <Link to="/testdata" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Fixtures
            </Link>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 sm:p-6 lg:p-8 min-w-0">
          <div className="max-w-4xl w-full min-w-0">
            {/* Back link for narrow screens */}
            <Link to="/testdata" className="lg:hidden block mb-4 text-sm text-gray-700 hover:text-gray-900 flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Fixtures
            </Link>
            <div className="mb-8">
              <h1 className="text-2xl font-semibold mb-2">Data Fixtures</h1>
              <p className="text-gray-600">
                Data Fixtures added to the Team can be used by all, or a subset of, your Test Suites.{" "}
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-8">
              {/* Basic Information */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="fixtureName" className="block text-sm font-medium mb-1">
                    Fixture Name
                  </label>
                  <input
                    type="text"
                    id="fixtureName"
                    value={fixture.name}
                    onChange={(e) => handleFixtureChange('name', e.target.value)}
                    placeholder="e.g., User Authentication"
                    className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="fixtureDescription" className="block text-sm font-medium mb-1">
                    Description
                  </label>
                  <textarea
                    id="fixtureDescription"
                    value={fixture.description}
                    onChange={(e) => handleFixtureChange('description', e.target.value)}
                    placeholder="Describe what this fixture is used for..."
                    rows={3}
                    className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Test Environments */}
              <div className="space-y-4">
                <label className="block text-sm font-medium mb-1">Test Environments</label>
                <select
                  value={environments}
                  onChange={(e) => setEnvironments(e.target.value)}
                  className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Production, Preview, and Development</option>
                  <option value="production">Production</option>
                  <option value="preview">Preview</option>
                  <option value="development">Development</option>
                </select>
              </div>

              {/* Data Items */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-8 gap-4 text-sm font-medium text-gray-700">
                  <div className="md:col-span-3">Key</div>
                  <div className="md:col-span-4">Value</div>
                  <div className="hidden md:block md:col-span-1"></div>
                </div>

                {fixture.data.map((item, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-8 gap-4 items-center group">
                    <div className="md:col-span-3 min-w-0">
                      <input
                        type="text"
                        value={item.key}
                        onChange={(e) => handleDataItemChange(index, 'key', e.target.value)}
                        placeholder="KEY_NAME"
                        className="w-full min-w-0 px-3 py-2 font-mono text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="md:col-span-4 min-w-0">
                      <input
                        type="text"
                        value={item.value}
                        onChange={(e) => handleDataItemChange(index, 'value', e.target.value)}
                        placeholder="Enter value..."
                        className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex justify-end md:col-span-1 md:justify-end">
                      {fixture.data.length > 1 && (
                        <button
                          className="p-2 text-gray-500 hover:text-red-500 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                          onClick={() => handleRemoveDataItem(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <button 
                  onClick={handleAddDataItem} 
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another
                </button>
              </div>

              {/* Link to Test Suites */}
              <div className="space-y-4">
                <label className="block text-sm font-medium mb-1">Link To Test Suites</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search for a Test Suite to link to..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full min-w-0 pl-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {loadingSuites ? (
                  <div className="py-4 text-center text-gray-500">
                    Loading test suites...
                  </div>
                ) : testSuites.length === 0 ? (
                  <div className="py-4 text-center text-gray-500">
                    No test suites found
                  </div>
                ) : (
                  <div className="mt-2 border border-gray-200 rounded-md max-h-60 overflow-y-auto">
                    {filteredTestSuites.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        No matching test suites found
                      </div>
                    ) : (
                      <ul className="divide-y divide-gray-200">
                        {filteredTestSuites.map((suite) => (
                          <li 
                            key={suite.id}
                            className={`px-4 py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer ${
                              selectedSuites.includes(suite.id) ? 'bg-blue-50' : ''
                            }`}
                            onClick={() => toggleSuiteSelection(suite.id)}
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">{suite.name}</p>
                              <p className="text-xs text-gray-500">{suite.id}</p>
                            </div>
                            {selectedSuites.includes(suite.id) && (
                              <Check className="w-5 h-5 text-blue-600" />
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {selectedSuites.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600">
                    {selectedSuites.length} test suite{selectedSuites.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </div>

              {/* Save Button */}
              <div className="flex flex-wrap justify-end gap-2 pt-6">
                <button
                  onClick={handleSaveFixture}
                  disabled={isLoading}
                  className={`px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-md ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTestData;
